/**
 * The REST surface the client uses.
 *
 * Deliberately tiny: spec 8.4 puts everything in-match on the open socket
 * because it beats REST on latency, and leaves REST for out-of-match concerns
 * only. Today that is exactly one call — minting a friend-duel share token,
 * which has to exist before there is a socket to mint it on.
 *
 * Contract:
 *   POST /api/duel     { tier, gameMode?, courseId?, capacity? }  ->  { token }
 *   POST /api/landing  { ref }                        ->  204
 *
 * The duel token is single-attempt and server-consumed (spec 8.7).
 */

import type { GameMode, Tier } from '@geogame/shared';

function apiBase(): string {
  const configured = import.meta.env['VITE_API_URL'];
  // Same-origin by default: `/api` is proxied to the gateway in dev and served
  // beside the bundle in production.
  return typeof configured === 'string' && configured.length > 0 ? configured : '';
}

export interface DuelInvite {
  readonly token: string;
  readonly url: string;
}

/**
 * Mint a duel link.
 *
 * Everything that decides what the duel IS goes into the SIGNED TOKEN, not into
 * the share URL — the joiner's screen never has to be told what it is opening,
 * and cannot be talked out of it by an edited link. `country` is sent as the
 * omitted default so an old client and a new one mint the same token for the
 * same duel.
 *
 * `courseId` is country-only; the server refuses the pair rather than dropping
 * it, so passing one alongside another mode is a 400 and not a surprise at the
 * far end.
 */
export async function createDuel(
  tier: Tier,
  gameMode: GameMode = 'country',
  courseId?: string,
  capacity = 2,
): Promise<DuelInvite> {
  const res = await fetch(`${apiBase()}/api/duel`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      tier,
      gameMode,
      ...(courseId !== undefined && courseId !== '' ? { courseId } : {}),
      // Omitted at 2 so a duel link keeps the exact token shape it always had.
      ...(capacity !== 2 ? { capacity } : {}),
    }),
  });
  if (!res.ok) throw new Error(`duel request failed (${res.status})`);
  const body = (await res.json()) as unknown;
  if (
    typeof body !== 'object' ||
    body === null ||
    typeof (body as { token?: unknown }).token !== 'string'
  ) {
    throw new Error('duel response was malformed');
  }
  const token = (body as { token: string }).token;
  return { token, url: `${window.location.origin}/d/${token}` };
}

/**
 * Tell the gateway a page was opened, once per tab.
 *
 * WHY THE GATEWAY AND NOT AN ANALYTICS PRODUCT. Because there is no third-party
 * host in this app and there is not going to be one — `nohosts.test.ts` fails
 * the suite on one, and an analytics tag on a page that shows you a round is a
 * stranger watching the round. The gateway is already the only host this client
 * speaks to, so it is the only honest place to count.
 *
 * WHAT IS SENT. The referrer's ORIGIN, and nothing else. `document.referrer` is
 * a full URL and can carry a path and a query string; those are somebody else's
 * business and are cut off here, before the network, rather than trusted to be
 * dropped at the other end. The server re-parses and re-checks it anyway.
 *
 * ONCE PER TAB, via `sessionStorage`: without the guard this counts reloads and
 * every restored background tab, which would quietly inflate the one number the
 * whole point of this was to measure honestly.
 *
 * FAILS SILENTLY, ALWAYS. `keepalive` so it survives the navigation that
 * usually follows, and every error is swallowed — a counter must never be able
 * to affect a page load, and the person is here to play.
 */
const LANDING_KEY = 'gg.landed';

export function reportLanding(): void {
  try {
    if (sessionStorage.getItem(LANDING_KEY) !== null) return;
    sessionStorage.setItem(LANDING_KEY, '1');
  } catch {
    // Private mode, or storage disabled. Counting twice is better than throwing
    // on the first line of the app, so fall through and send it.
  }

  let ref = '';
  try {
    ref = document.referrer === '' ? '' : new URL(document.referrer).origin;
  } catch {
    ref = '';
  }

  // BELT AND BRACES, because of WHERE this is called from.
  //
  // `main.tsx` calls this at module scope, before `createRoot().render()`. The
  // `.catch()` handles a rejected request, but a SYNCHRONOUS throw here — no
  // `fetch` on an ancient engine, a policy that makes the constructor throw —
  // would take the render call with it, and the cost of a counter failing would
  // be a white screen instead of a missing row.
  try {
    void fetch(`${apiBase()}/api/landing`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ref }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* nothing to do, and nothing worth doing */
  }
}

// ---------------------------------------------------------------------------
// Signing in
// ---------------------------------------------------------------------------

/**
 * WHO IS SIGNING IN.
 *
 * REST has no session, so every call here carries the identity token the
 * socket handed this browser in `welcome`. That token is already what the
 * gateway trusts to say who a reconnecting player is, so trusting it to say
 * who is signing in adds no new authority — and it means an account attaches
 * to the player this device ALREADY IS rather than minting a new one.
 */
export interface SignedIn {
  readonly playerId: string;
  readonly handle: string;
  readonly email: string;
  /** Replaces the stored identity token. May name a different player. */
  readonly token: string;
  /** True when an anonymous history on this device was folded into an account. */
  readonly merged: boolean;
}

export interface AuthProviders {
  readonly google: boolean;
  readonly email: boolean;
}

export async function authProviders(): Promise<AuthProviders> {
  try {
    const res = await fetch(`${apiBase()}/api/auth/providers`);
    if (!res.ok) return { google: false, email: true };
    return (await res.json()) as AuthProviders;
  } catch {
    // A gateway that cannot answer is a gateway that cannot sign anybody in
    // either. Email stays true so the form renders and fails honestly on send.
    return { google: false, email: true };
  }
}

/**
 * Ask for a magic link.
 *
 * ALWAYS RESOLVES THE SAME WAY for any address the server accepted, because
 * the server answers the same way: a response that distinguished a known
 * address from an unknown one would be an oracle for who has an account.
 */
export async function requestMagicLink(email: string, deviceToken: string): Promise<void> {
  const res = await fetch(`${apiBase()}/api/auth/magic/request`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    // Omitted rather than blanked when this browser has never played: the
    // gateway reads absent as "mint me a player" and a forged value as an
    // attack, and an empty string should not have to mean the first.
    body: JSON.stringify(deviceToken === '' ? { email } : { email, deviceToken }),
  });
  if (!res.ok) throw new Error(res.status === 400 ? 'bad_email' : 'send_failed');
}

/** Redeem a link. The token comes out of the URL fragment. */
export async function consumeMagicLink(token: string, deviceToken: string): Promise<SignedIn> {
  const res = await fetch(`${apiBase()}/api/auth/magic/consume`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(deviceToken === '' ? { token } : { token, deviceToken }),
  });
  if (!res.ok) throw new Error(res.status === 401 ? 'bad_link' : 'sign_in_failed');
  return (await res.json()) as SignedIn;
}

/**
 * Where the Google round trip starts.
 *
 * A full page navigation rather than a fetch, because the consent screen is a
 * page. The device token rides the query string so the state the server mints
 * is bound to THIS browser: a state that could be finished anywhere is a way
 * to land somebody else's account on your device.
 */
export function googleSignInUrl(deviceToken: string): string {
  return `${apiBase()}/api/auth/google/start?deviceToken=${encodeURIComponent(deviceToken)}`;
}

// ---------------------------------------------------------------------------
// Progression
// ---------------------------------------------------------------------------

/**
 * WHY THESE CARRY A DEVICE TOKEN. The two `/api/me` routes answer about
 * whoever holds it, the same token sign-in uses, so a player can only ask
 * about themselves. The board is public and takes none.
 */
export interface CoverageEntity {
  readonly code: string;
  readonly seen: number;
  readonly correct: number;
  readonly lastSeenAt: number;
}

/** Entity CODES, not names. The client already holds the country table. */
/**
 * Which of today's three dailies this browser has already spent.
 *
 * NEVER THROWS, and returns an empty verdict rather than a guess. This decides
 * whether a card says "new today", and a card that wrongly claims a fresh
 * daily sends somebody to a screen that turns them away -- so an unknown
 * answer has to be distinguishable from a "no". `undefined` per mode means we
 * do not know; only an explicit `false` prints the badge.
 */
export async function fetchDailyStatus(
  deviceToken: string | null,
): Promise<Partial<Record<GameMode, boolean>>> {
  if (deviceToken === null) return {};
  try {
    const res = await fetch(
      `${apiBase()}/api/daily/status?token=${encodeURIComponent(deviceToken)}`,
    );
    if (!res.ok) return {};
    const body = (await res.json()) as { played?: Record<string, boolean> };
    return body.played ?? {};
  } catch {
    return {};
  }
}

export async function fetchCoverage(
  mode: GameMode,
  deviceToken: string,
): Promise<readonly CoverageEntity[]> {
  const url = `${apiBase()}/api/me/coverage?mode=${mode}&deviceToken=${encodeURIComponent(deviceToken)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(res.status === 401 ? 'no_device' : 'coverage_failed');
  return ((await res.json()) as { entities: CoverageEntity[] }).entities;
}

export interface Rival {
  readonly playerId: string;
  readonly handle: string;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
  readonly lastPlayedAt: number;
}

export async function fetchRivals(deviceToken: string): Promise<readonly Rival[]> {
  const url = `${apiBase()}/api/me/rivals?deviceToken=${encodeURIComponent(deviceToken)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(res.status === 401 ? 'no_device' : 'rivals_failed');
  return ((await res.json()) as { rivals: Rival[] }).rivals;
}

export interface BoardRow {
  readonly rank: number;
  readonly handle: string;
  readonly best: number;
  readonly total: number;
  readonly matches: number;
}

export async function fetchBoard(
  mode: GameMode,
  window: 'week' | 'all',
): Promise<readonly BoardRow[]> {
  const res = await fetch(`${apiBase()}/api/leaderboard?mode=${mode}&window=${window}`);
  if (!res.ok) throw new Error('board_failed');
  return ((await res.json()) as { rows: BoardRow[] }).rows;
}
